import {
  ReportAggregation,
  ReportAggregationMode,
  ReportColumn,
  ReportDefinition,
  ReportDynamicFilter,
  ReportFieldType,
  ReportFilterOperator,
  ReportFilterValue,
  ReportMetadataField,
  ReportMetadataFilter,
  ReportTimeRangePreset,
} from '../../core/models/report.model';

/**
 * The flat, Formly-friendly model backing the report builder wizard, and the
 * pure mapping from that model to a {@link ReportDefinition}.
 *
 * Column/target references are encoded as `"<source>::<key>"` strings so a
 * single Formly select/multi-checkbox can address both metadata and dynamic
 * fields; {@link decodeFieldRef} turns them back into structured refs.
 */
export interface ReportBuilderModel {
  name: string;
  formId?: string;
  timeRangePreset: ReportTimeRangePreset;
  /** ISO-ish values from the datetime inputs; only used when preset = custom. */
  customStart?: string;
  customEnd?: string;
  /** Encoded column refs, e.g. "metadata::created_at", "dynamic::hours". */
  columns: string[];
  sortColumn?: string;
  sortDirection: 'asc' | 'desc';
  aggregationMode: ReportAggregationMode;
  /** Encoded field ref used as the aggregation target (sum/avg/min/max/distinct_count). */
  aggregationTarget?: string;
  /** Encoded field ref to group an aggregate by (optional). */
  groupByColumn?: string;
  /** User-defined filter rows (metadata + dynamic fields). */
  filters: ReportFilterRow[];
  /** Keep only the latest N submissions per checkpoint-instance. */
  latestPerInstanceEnabled: boolean;
  latestPerInstanceN?: number;
  /** "Latest at or before" cutoff (datetime input value). */
  asOf?: string;
}

/**
 * One filter row in the builder. `field` is an encoded field ref; `value` is the
 * raw string from the input (comma-separated for `in`/`not_in`/`between`).
 */
export interface ReportFilterRow {
  field?: string;
  operator?: ReportFilterOperator;
  /**
   * Raw value from the value editor. A `string[]` for enum `in`/`not_in`
   * checklists; a `string` for scalar inputs (comma-separated for `in`/`not_in`
   * / `between` on non-enum fields).
   */
  value?: string | string[];
}

/** A field the builder can offer as a column / sort / aggregation target. */
export interface AvailableField {
  /** Encoded ref used as the Formly option value. */
  ref: string;
  label: string;
  source: 'metadata' | 'dynamic';
  key: string;
  fieldType: ReportFieldType;
  /** Present for enum fields — the selectable option values and labels. */
  options?: { label: string; value: string | number }[];
}

export function encodeFieldRef(source: 'metadata' | 'dynamic', key: string): string {
  return `${source}::${key}`;
}

export function decodeFieldRef(ref: string): { source: 'metadata' | 'dynamic'; key: string } {
  const [source, ...rest] = ref.split('::');
  return { source: source === 'dynamic' ? 'dynamic' : 'metadata', key: rest.join('::') };
}

export function emptyBuilderModel(): ReportBuilderModel {
  return {
    name: '',
    timeRangePreset: 'all_time',
    columns: [],
    sortDirection: 'desc',
    aggregationMode: 'none',
    filters: [],
    latestPerInstanceEnabled: false,
  };
}

/** Operators that take no value (the value input is hidden). */
export const VALUELESS_OPERATORS: ReportFilterOperator[] = ['is_empty', 'is_not_empty'];

/** Operators whose value is a comma-separated list / pair. */
const LIST_OPERATORS: ReportFilterOperator[] = ['in', 'not_in'];
const PAIR_OPERATORS: ReportFilterOperator[] = ['between'];

/** Coerces a single raw string to the field's value type. */
function coerceScalar(raw: string, fieldType: ReportFieldType): string | number | boolean {
  const trimmed = raw.trim();
  if (fieldType === 'number') {
    const n = Number(trimmed);
    return Number.isNaN(n) ? trimmed : n;
  }
  if (fieldType === 'boolean') {
    return trimmed === 'true' || trimmed === '1';
  }
  return trimmed;
}

/**
 * Splits a raw row value into trimmed, non-empty string parts. An array value
 * (enum checklist) is used as-is; a string is split on commas.
 */
function valueParts(raw: string | string[]): string[] {
  const parts = Array.isArray(raw) ? raw : raw.split(',');
  return parts.map((v) => String(v).trim()).filter((v) => v.length > 0);
}

/** Builds a filter value from the row's raw value, per operator semantics. */
function buildFilterValue(
  row: ReportFilterRow,
  operator: ReportFilterOperator,
  fieldType: ReportFieldType,
): ReportFilterValue {
  if (VALUELESS_OPERATORS.includes(operator)) return null;

  const raw = row.value ?? '';
  if (LIST_OPERATORS.includes(operator)) {
    return valueParts(raw).map((v) => coerceScalar(v, fieldType)) as Array<string | number>;
  }
  if (PAIR_OPERATORS.includes(operator)) {
    const [lo = '', hi = ''] = valueParts(raw);
    return [coerceScalar(lo, fieldType), coerceScalar(hi, fieldType)] as [
      string | number,
      string | number,
    ];
  }
  // Scalar operators use the whole value (arrays collapse to their first entry).
  const scalar = Array.isArray(raw) ? (raw[0] ?? '') : raw;
  return coerceScalar(scalar, fieldType);
}

/** A filter row is usable once it has a field, an operator, and (if the
 * operator needs one) a value. */
export function isCompleteFilterRow(row: ReportFilterRow): boolean {
  if (!row.field || !row.operator) return false;
  if (VALUELESS_OPERATORS.includes(row.operator)) return true;
  return valueParts(row.value ?? '').length > 0;
}

/**
 * Splits completed filter rows into metadata and dynamic filters for the query
 * engine, resolving each row's field type from `available`.
 */
export function filterRowsToDefinitionFilters(
  rows: ReportFilterRow[],
  available: AvailableField[],
): { metadataFilters: ReportMetadataFilter[]; dynamicFilters: ReportDynamicFilter[] } {
  const byRef = new Map(available.map((f) => [f.ref, f]));
  const metadataFilters: ReportMetadataFilter[] = [];
  const dynamicFilters: ReportDynamicFilter[] = [];

  for (const row of rows) {
    if (!isCompleteFilterRow(row) || !row.field || !row.operator) continue;
    const field = byRef.get(row.field);
    if (!field) continue;

    const value = buildFilterValue(row, row.operator, field.fieldType);

    if (field.source === 'metadata') {
      metadataFilters.push({
        field: field.key as ReportMetadataField,
        fieldType: field.fieldType,
        operator: row.operator,
        value,
      });
    } else {
      dynamicFilters.push({
        fieldKey: field.key,
        fieldType: field.fieldType,
        operator: row.operator,
        value,
      });
    }
  }

  return { metadataFilters, dynamicFilters };
}

/**
 * Standard metadata fields offered by the builder for a single-form report.
 *
 * `checkpoint` and `created_by` are enum fields: their selectable options
 * (checkpoint names / user names) are supplied by the caller so the filter value
 * editor can present a checklist instead of a raw id text box.
 */
export function metadataFields(opts?: {
  checkpoints?: { label: string; value: string | number }[];
  users?: { label: string; value: string | number }[];
}): AvailableField[] {
  return [
    {
      ref: encodeFieldRef('metadata', 'created_at'),
      label: 'reports.field.createdAt',
      source: 'metadata',
      key: 'created_at',
      fieldType: 'date',
    },
    {
      ref: encodeFieldRef('metadata', 'checkpoint'),
      label: 'reports.field.checkpoint',
      source: 'metadata',
      key: 'checkpoint',
      fieldType: 'enum',
      options: opts?.checkpoints ?? [],
    },
    {
      ref: encodeFieldRef('metadata', 'created_by'),
      label: 'reports.field.createdBy',
      source: 'metadata',
      key: 'created_by',
      fieldType: 'enum',
      options: opts?.users ?? [],
    },
  ];
}

/**
 * Translates the builder model into a runnable {@link ReportDefinition}.
 * `available` provides the field-type for each encoded ref so columns/sort carry
 * correct types into the query engine.
 */
export function modelToDefinition(
  model: ReportBuilderModel,
  available: AvailableField[],
): ReportDefinition {
  const byRef = new Map(available.map((f) => [f.ref, f]));

  const selectedColumns: ReportColumn[] = (model.columns ?? [])
    .map((ref) => byRef.get(ref))
    .filter((f): f is AvailableField => !!f)
    .map((f) => ({
      source: f.source,
      key: f.key,
      fieldType: f.fieldType,
      // Metadata labels are i18n keys (translated by columnLabel()); dynamic
      // labels are literal field names and must be carried through here.
      ...(f.source === 'dynamic' ? { label: f.label } : {}),
    }));

  const sortField = model.sortColumn ? byRef.get(model.sortColumn) : undefined;
  const sort = sortField
    ? [{ source: sortField.source, key: sortField.key, direction: model.sortDirection }]
    : [];

  const timeRange =
    model.timeRangePreset === 'custom'
      ? {
          preset: 'custom' as const,
          startMs: model.customStart ? new Date(model.customStart).getTime() : undefined,
          endMs: model.customEnd ? new Date(model.customEnd).getTime() : undefined,
        }
      : { preset: model.timeRangePreset };

  // count needs no target; every other non-none mode targets a field.
  const modeNeedsTarget =
    model.aggregationMode !== 'none' && model.aggregationMode !== 'count';
  const aggTarget =
    modeNeedsTarget && model.aggregationTarget ? byRef.get(model.aggregationTarget) : undefined;
  const aggregation: ReportAggregation = aggTarget
    ? { mode: model.aggregationMode, targetKey: aggTarget.key, targetSource: aggTarget.source }
    : { mode: model.aggregationMode };

  // group-by only applies to aggregate reports.
  const groupField =
    model.aggregationMode !== 'none' && model.groupByColumn
      ? byRef.get(model.groupByColumn)
      : undefined;
  const groupBy = groupField
    ? {
        source: groupField.source,
        key: groupField.key,
        // Metadata labels are i18n keys (translated by columnLabel()); dynamic
        // labels are literal field names and must be carried through here.
        ...(groupField.source === 'dynamic' ? { label: groupField.label } : {}),
      }
    : undefined;

  const { metadataFilters, dynamicFilters } = filterRowsToDefinitionFilters(
    model.filters ?? [],
    available,
  );

  return {
    name: model.name?.trim() || 'Untitled Report',
    formScope: 'single_form',
    formId: model.formId,
    // Checkpoint / user scoping is now expressed as ordinary enum filter rows on
    // the `checkpoint` / `created_by` metadata fields (see metadataFields), so
    // the dedicated scope selectors are no longer emitted by the builder.
    timeRange,
    latestPerInstance: model.latestPerInstanceEnabled
      ? { enabled: true, n: Math.max(1, model.latestPerInstanceN ?? 1) }
      : undefined,
    asOfTimestampMs: model.asOf ? new Date(model.asOf).getTime() : undefined,
    metadataFilters,
    dynamicFilters,
    selectedColumns,
    sort,
    aggregation,
    groupBy,
    visibility: 'private',
  };
}

/**
 * Inverse of {@link modelToDefinition}: hydrates a builder model from a saved
 * definition so an existing report can be edited.
 */
export function definitionToModel(def: ReportDefinition): ReportBuilderModel {
  const model = emptyBuilderModel();
  model.name = def.name ?? '';
  model.formId = def.formId;
  model.timeRangePreset = def.timeRange?.preset ?? 'all_time';
  if (def.timeRange?.preset === 'custom') {
    model.customStart = def.timeRange.startMs ? new Date(def.timeRange.startMs).toISOString() : undefined;
    model.customEnd = def.timeRange.endMs ? new Date(def.timeRange.endMs).toISOString() : undefined;
  }
  model.columns = (def.selectedColumns ?? []).map((c) => encodeFieldRef(c.source, c.key));
  const firstSort = def.sort?.[0];
  if (firstSort) {
    model.sortColumn = encodeFieldRef(firstSort.source, firstSort.key);
    model.sortDirection = firstSort.direction;
  }
  model.aggregationMode = def.aggregation?.mode ?? 'none';
  if (def.aggregation?.targetKey) {
    model.aggregationTarget = encodeFieldRef(def.aggregation.targetSource ?? 'dynamic', def.aggregation.targetKey);
  }
  if (def.groupBy) {
    model.groupByColumn = encodeFieldRef(def.groupBy.source, def.groupBy.key);
  }
  // Legacy scope selectors are migrated into equivalent `in` filter rows so
  // reports saved before checkpoint/user scoping moved into the Filters tab keep
  // working and become editable there.
  const legacyScopeRows: ReportFilterRow[] = [];
  if (def.checkpointIds?.length) {
    legacyScopeRows.push({
      field: encodeFieldRef('metadata', 'checkpoint'),
      operator: 'in',
      value: def.checkpointIds.map((id) => String(id)),
    });
  }
  if (def.userIds?.length) {
    legacyScopeRows.push({
      field: encodeFieldRef('metadata', 'created_by'),
      operator: 'in',
      value: def.userIds.map((id) => String(id)),
    });
  }

  model.filters = [
    ...legacyScopeRows,
    ...(def.metadataFilters ?? []).map((f) => ({
      field: encodeFieldRef('metadata', f.field),
      operator: f.operator,
      value: filterValueToRow(f.value, f.operator),
    })),
    ...(def.dynamicFilters ?? []).map((f) => ({
      field: encodeFieldRef('dynamic', f.fieldKey),
      operator: f.operator,
      value: filterValueToRow(f.value, f.operator),
    })),
  ];
  model.latestPerInstanceEnabled = !!def.latestPerInstance?.enabled;
  model.latestPerInstanceN = def.latestPerInstance?.n;
  model.asOf = def.asOfTimestampMs ? new Date(def.asOfTimestampMs).toISOString() : undefined;
  return model;
}

/**
 * Renders a stored filter value back to the builder's row form. `in`/`not_in`
 * values become a `string[]` so the enum checklist can pre-check them; scalar
 * values become a comma-joined string.
 */
function filterValueToRow(
  value: unknown,
  operator: ReportFilterOperator,
): string | string[] {
  if (operator === 'in' || operator === 'not_in') {
    const list = Array.isArray(value) ? value : value == null ? [] : [value];
    return list.map((v) => String(v));
  }
  if (value == null) return '';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}
